/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEvent,
  MouseEventHandler,
  useCallback
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {TertiaryIconButton} from '@ion/dls/src/components/buttons/TertiaryIconButton';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import RadioButton from '../components/radio/RadioButton';
import Render from '../render/Render';

export interface NotificationOptionBarProps {
  onSeeUnreadOnly?: () => void;
  onSeeAll?: () => void;
  handleSelectAll?: (event: MouseEvent<HTMLButtonElement>) => void;
  selectedRows: Array<any> | [];
  urgentNotifications?: any;
  allNotifications?: any;
  value?: number;
  filterAll?: boolean;
  filterUnread?: boolean;
  notificationsCheckAll?: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    notificationOptionActionsRow : {
      top            : '10px',
      display        : 'flex',
      position       : 'relative',
      alignItems     : 'center',
      justifyContent : 'space-between'
    },
    notificationCheckbox : {
      marginLeft : '24px'
    },
    // RadioButton's own FormControlLabel label falls back to MUI's global
    // body1 typography (16px/22px) - Figma specs 14px/16px for this text, so
    // override just the label slot via RadioButton's own `labelClasses` prop
    // rather than touching the shared component's base style ...
    radioLabel : {
      fontFamily : 'Inter',
      fontWeight : 400,
      fontSize   : '14px',
      lineHeight : '16px',
      color      : dlsColor('grey1')
    }
  };
});

// TertiaryIconButton's own shared label style sets lineHeight:'14px' - Figma
// specs 16px for this "select/deselect all" text, so override just that via
// its own `labelStyle` prop (a plain style object, not a class) rather than
// touching the shared component ...
const selectAllLabelStyle = {
  lineHeight : '16px'
};

/**
 * Component for rendering notification option bar.
 *
 * ```typescript
 * 
 * import {NotificationOptionBar} from '@ion/dls/src/notifications'
 * 
 * ```
 *
 */
export const NotificationOptionBar = ({
  handleSelectAll,
  onSeeUnreadOnly,
  onSeeAll,
  value,
  selectedRows,
  urgentNotifications,
  allNotifications,
  filterAll = false,
  filterUnread = false,
  notificationsCheckAll = false
}: NotificationOptionBarProps) => {
  const classes = useStyles();

  const hasNotifications = value === 0
    ? urgentNotifications?.length
    : allNotifications?.length;

  const onActionWithSelectedRows = useCallback((callback): MouseEventHandler =>
    () => callback?.(selectedRows),
  [selectedRows]);

  return (
    <>
      <div className={classes.notificationOptionActionsRow}>
        <div className={classes.notificationCheckbox}>
          <RadioButton label={translate('notifications:panel.menu.all')}
            checked={filterAll}
            labelClasses={{label: classes.radioLabel}}
            onChange={onSeeAll} />
          <RadioButton label={translate('notifications:panel.menu.unread')}
            checked={filterUnread}
            labelClasses={{label: classes.radioLabel}}
            onChange={onSeeUnreadOnly} />
        </div>
        <Render.If condition={hasNotifications}>
          <div>
            <TertiaryIconButton label={translate(`grids:selectionsHeader.${notificationsCheckAll ? 'deselectAll' : 'selectAll'}`)}
              labelStyle={selectAllLabelStyle}
              onClick={onActionWithSelectedRows(handleSelectAll)} />
          </div>
        </Render.If>
      </div>
    </>
  );
};

export default React.memo(NotificationOptionBar);
